import React, { FC, useEffect, useState } from 'react';

import { NavLink } from 'react-router-dom';

import Button from 'components/common/ui-kit/Button';
import Checkbox from 'components/common/ui-kit/Checkbox';
import Loader from 'components/common/ui-kit/Loader';
import ModeratorFile from 'components/moderator/ModeratorFile';
import EntrantsContractForm from 'components/statements/CreateContractForm/EntrantsContractForm';
import JuridicalContractForm from 'components/statements/CreateContractForm/JuridicalContractForm';
import NotEntrantsContractForm from 'components/statements/CreateContractForm/NotEntrantsContractForm';
import useAppDispatch from 'hooks/common/useAppDispatch';
import useAppSelector from 'hooks/common/useAppSelector';
import useModeratorContractIdInfoForm from 'hooks/moderator/useModeratorContractIdInfoForm';
import terms, { getTranslationOrEmptyString } from 'i18n';
import { UserRole } from 'models/user';
import { showModal } from 'redux/actions';
import { contractApi } from 'redux/api/applications/contract-api';
import { selectUser } from 'redux/selectors';
import { formatDateTimeToLocaleString } from 'utils/date';

import { useDownloadFile } from '../../../hooks/common/api/useDownloadFile';
import {
  ContractState,
  ContractType,
  PaymentMethod,
} from '../../../models/applications/contracts';
import { selectCurrentStaffContract } from '../../../redux/selectors/staff/contracts';
import { buildContractNameFromDirectionProperties } from '../../../utils/domain/direction';
import { savePreviousLocation } from '../../../utils/domain/impersonate';
import ModeratorContractIdInfoEntrants from '../ModeratorContractIdInfoEntrants';
import ModeratorContractIdInfoJuridical from '../ModeratorContractIdInfoJuridical';
import ModeratorContractIdInfoNotEntrants from '../ModeratorContractIdInfoNotEntrants';

const ModeratorContractIdInfo: FC = () => {
  const {
    control,
    reject,
    approve,
    downloadPrintContract,
    downloadTemplate,
    takeToWork,
    sendForEditing,
    receive,
    confirm,
    cancel,
    downloadPrintCustomerConsent,
    downloadPrintMaternalCapitalSupplemental,
    downloadPrintInvoice,
    downloadPrintPaymentRequest,
    sspvoLink,
  } = useModeratorContractIdInfoForm();

  const dispatch = useAppDispatch();

  const { role } = useAppSelector(selectUser);

  const currentStaffContract = useAppSelector(selectCurrentStaffContract);
  const downloadFile = useDownloadFile();
  const [isEditing, setEditing] = useState(false);
  const [getContract] = contractApi.useLazyGetByIdQuery();

  const [signedReceived, setSignedReceived] = useState(false);
  const [copyGiven, setCopyGiven] = useState(false);
  const [updateCheckboxesCon] = contractApi.useUpdateContractMutation();

  useEffect(() => {
    if (currentStaffContract?.contract) {
      setSignedReceived(
        currentStaffContract.contract.signedContractReceived ?? false
      );
      setCopyGiven(
        currentStaffContract.contract.contractCopyGivenToApplicant ?? false
      );
    }
  }, [currentStaffContract]);

  if (!currentStaffContract) {
    return <Loader />;
  }

  const questionnaire = currentStaffContract.questionnaire;
  const contract = currentStaffContract?.contract;

  if (!contract) {
    return <Loader />;
  }

  const handleCheckboxChange = (
    field: 'signedContractReceived' | 'contractCopyGivenToApplicant',
    value: boolean
  ) => {
    if (field === 'signedContractReceived') {
      setSignedReceived(value);
    }
    if (field === 'contractCopyGivenToApplicant') {
      setCopyGiven(value);
    }

    updateCheckboxesCon({
      id: contract.id,
      applicationDirectionId: contract.applicationDirectionId,
      contractType: contract.contractType,
      paymentTypeId: contract.paymentType.id,
      contractDate: contract.contractDate,
      [field]: value,
      signedContractReceived:
        field === 'signedContractReceived' ? value : signedReceived,
      contractCopyGivenToApplicant:
        field === 'contractCopyGivenToApplicant' ? value : copyGiven,
      contractThirdPartyPayerData: contract.thirdPartyPayerData,
      contractLegalEntityPayerData: contract.legalEntityPayerData,
    });
  };

  const user = currentStaffContract.user;
  const onImpersonateClick = () => {
    savePreviousLocation();
  };

  if (isEditing) {
    return contract.contractType === ContractType.SELF_PAYMENT ? (
      <EntrantsContractForm
        contract={contract}
        isFillingStatementsForm={false}
        onSubmit={() => {
          getContract(contract.id)
            .unwrap()
            .then(() => setEditing(false));
        }}
      />
    ) : contract.contractType === ContractType.JURIDICAL ? (
      <JuridicalContractForm
        contract={contract}
        isFillingStatementsForm={false}
        onSubmit={() => {
          getContract(contract.id)
            .unwrap()
            .then(() => setEditing(false));
        }}
      />
    ) : (
      <NotEntrantsContractForm
        contract={contract}
        isFillingStatementsForm={false}
        onSubmit={() => {
          getContract(contract.id)
            .unwrap()
            .then(() => setEditing(false));
        }}
      />
    );
  }

  const contractName = buildContractNameFromDirectionProperties({
    title: contract?.directionTitle ?? '',
    titleForeign: contract?.directionTitleForeign ?? '',
    code: contract?.directionCode ?? '',
    educationProgramCode: contract?.educationProgramCode ?? '',
    educationProgramTitle: contract?.educationProgramTitle ?? '',
    educationProgramTitleForeign: contract?.educationProgramTitleForeign ?? '',
    educationForm: contract?.directionEducationForm ?? {
      title: '',
      titleForeign: '',
    },
  });

  return (
    <div className="moderator-contract-info">
      <div className="moderator-contract-info__block">
        <h2>
          <span>
            {`${questionnaire.generalDataForm.surname || ''} ${
              questionnaire.generalDataForm.name
            } ${questionnaire.generalDataForm.patronymic ?? ''}`}
          </span>
        </h2>
        <p>
          ID: <span>{user.idWithPrefix}</span>
        </p>
        <p>
          Вид образования:{' '}
          <span>
            {getTranslationOrEmptyString(
              questionnaire.educationalInformationForm[0].educationLevel
            )}
          </span>
        </p>
        <p>
          {terms.CONTRACT_INFORMING}:{' '}
          <span>{contract.informing ? 'Да' : 'Нет'}</span>
        </p>
      </div>
      <div className="moderator-contract-info__block">
        <h2>
          Направление: <span>{contractName}</span>
        </h2>
        <p>
          Подразделение: <span></span>
        </p>
        <p>
          Статус договора:{' '}
          <span>{getTranslationOrEmptyString(contract.state)}</span>
        </p>
        {contract.isOnline &&
          contract.state === ContractState.CANCELLED &&
          contract.isCancelledByEpgu !== null && (
            <p>
              Сторона, аннулировавшая договор:{' '}
              <span>{contract.isCancelledByEpgu ? 'ЕПГУ' : 'СПбПУ'}</span>
            </p>
          )}
        {contract.isOnline && (
          <p>
            Статус договора на ЕПГУ:
            <span>{contract.epguStatus ? contract.epguStatus.title : '-'}</span>
          </p>
        )}
        <p>
          {terms.CONTRACT_TYPE}:{' '}
          <span>
            {contract.isOnline
              ? terms.ELECTRONIC_CONTRACT
              : terms.PAPER_CONTRACT}
          </span>
        </p>
        <p>
          Тип платежа: <span>{contract.paymentType.title}</span>
        </p>
        <p>
          Плательщик:{' '}
          <span>{getTranslationOrEmptyString(contract.contractType)}</span>
        </p>
        {contract.contractType !== ContractType.JURIDICAL && (
          <p>
            Особенности оплаты:{' '}
            <span>{getTranslationOrEmptyString(contract.paymentMethod)}</span>
          </p>
        )}
        {contract.claim && (
          <p style={{ color: 'red' }}>
            Претензия поступающего с ЕПГУ: <span>{contract.claim}</span>
          </p>
        )}
      </div>

      {contract.contractType === ContractType.SELF_PAYMENT ? (
        <ModeratorContractIdInfoEntrants questionnaire={questionnaire} />
      ) : contract.contractType === ContractType.THIRD_PARTY ? (
        <ModeratorContractIdInfoNotEntrants
          contractNotEntrants={contract?.thirdPartyPayerData}
        />
      ) : contract.contractType === ContractType.JURIDICAL ? (
        <ModeratorContractIdInfoJuridical
          contractJuridical={contract?.legalEntityPayerData}
        />
      ) : (
        <></>
      )}

      {!contract.isOnline && (
        <div className="moderator-contract-info__block">
          <h2>Бумажный экземпляр контракта</h2>
          <Checkbox
            text="Контракт получен с подписью"
            checked={signedReceived}
            onChange={e =>
              handleCheckboxChange('signedContractReceived', e.target.checked)
            }
          />
          <Checkbox
            text="Экземпляр контракта передан абитуриенту"
            checked={copyGiven}
            onChange={e =>
              handleCheckboxChange(
                'contractCopyGivenToApplicant',
                e.target.checked
              )
            }
          />
        </div>
      )}

      <div className="moderator-contract-info__block">
        <h2>Документы и печатные формы:</h2>
        <div className="moderator-contract-info__block_file-container">
          <ModeratorFile
            title="Договор"
            upload={() => downloadPrintContract(contract)}
          />
          {contract.contractType === ContractType.THIRD_PARTY && (
            <ModeratorFile
              title="Согласие на обработку персональных данных (заказчика)"
              upload={() => downloadPrintCustomerConsent(contract)}
            />
          )}
          {contract.contractType !== ContractType.JURIDICAL && (
            <ModeratorFile
              title="Паспорт плательщика"
              upload={() => {
                if (!questionnaire.passportForm.passportCopyUploadId) return;

                downloadFile({
                  userId: user.id,
                  fileId:
                    contract.contractType === ContractType.THIRD_PARTY
                      ? contract.thirdPartyPayerData!.documentScanId
                      : questionnaire.passportForm.passportCopyUploadId,
                  fileName: 'Паспорт.pdf',
                });
              }}
            />
          )}
          {contract.isOnline && contract.signature && (
            <ModeratorFile
              title="Подписанный договор"
              upload={() => {
                downloadFile({
                  userId: user.id,
                  fileId: contract.signature!.signedFileId,
                  fileName: 'Договор_' + contract.number + '.pdf',
                });
              }}
            />
          )}
          {contract.isOnline && contract.signature && (
            <ModeratorFile
              title="Файл подписи СПбПУ"
              upload={() => {
                downloadFile({
                  userId: user.id,
                  fileId: contract.signature!.signatureFileId,
                  fileName: 'Договор_' + contract.number + '_СПбПУ.sig',
                });
              }}
            />
          )}
          {contract.paymentMethod === PaymentMethod.MATERNAL_CAPITAL && (
            <ModeratorFile
              title="Доп.соглашение об оплате за счет средств МСК"
              upload={() => downloadPrintMaternalCapitalSupplemental(contract)}
            />
          )}
          <ModeratorFile
            title="Счет на оплату"
            upload={() => downloadPrintInvoice(contract)}
          />
          <ModeratorFile
            title="Направление на оплату"
            upload={() => downloadPrintPaymentRequest(contract)}
          />
        </div>
      </div>

      {!isEditing && (
        <>
          {(contract.uploadDate || contract.uploadError) && (
            <div className="moderator-contract-info__block">
              {contract.uploadDate && (
                <p>
                  Выгрузка в 1С:{' '}
                  <span>
                    {formatDateTimeToLocaleString(contract.uploadDate)}
                  </span>
                </p>
              )}
              {contract.uploadError && (
                <p>
                  Ошибка загрузки в 1С: <span>{contract.uploadError}</span>
                </p>
              )}
            </div>
          )}
          <div className="moderator-contract-info__footer">
            <div className="moderator-contract-info__footer-btn-left">
              <div className="moderator-contract-info__footer-btn">
                <NavLink
                  to={`/admin/users/impersonate/${user.id}/${contract.applicationId}`}
                  target="_blank"
                  onClick={onImpersonateClick}
                >
                  <Button type="button">
                    Зайти в личный кабинет пользователя
                  </Button>
                </NavLink>
              </div>
              {user.registerFromEpgu &&
                (role === UserRole.MODERATOR || role === UserRole.ADMIN) && (
                  <div className="moderator-contract-info__footer-btn-left">
                    <div className="moderator-contract-info__footer-btn">
                      <a
                        href={sspvoLink()}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button type="button">
                          Зайти в личный кабинет пользователя ССПВО
                        </Button>
                      </a>
                    </div>
                  </div>
                )}
            </div>

            <div className="moderator-contract-info__management-btns">
              {contract.state === ContractState.PROJECT && (
                <Button onClick={() => takeToWork(contract.id)}>
                  Взять в работу
                </Button>
              )}

              {contract.isOnline && contract.state === ContractState.APPROVED && (
                <Button
                  onClick={() =>
                    dispatch(
                      showModal({
                        name: 'CHECK_SIGN_CODE',
                        data: { contractId: contract.id },
                      })
                    )
                  }
                  theme="success"
                >
                  Подписать
                </Button>
              )}

              {(contract.state === ContractState.UNDER_CONSIDERATION ||
                contract.state === ContractState.EDITED) && (
                <Button onClick={() => approve(contract.id)} theme="success">
                  Одобрить
                </Button>
              )}

              {(contract.state === ContractState.UNDER_CONSIDERATION ||
                contract.state === ContractState.EDITED ||
                contract.state === ContractState.EDITING) && (
                <Button
                  onClick={() => {
                    if (contract.state === ContractState.EDITING) {
                      setEditing(true);
                      return;
                    }
                    sendForEditing(contract.id)
                      .then(() => setEditing(true))
                      .finally(() => getContract(contract.id));
                  }}
                >
                  Редактировать
                </Button>
              )}

              {contract.state === ContractState.APPROVED && !contract.isOnline && (
                <Button onClick={() => receive(contract.id)} theme="success">
                  Договор получен
                </Button>
              )}

              {contract.state === ContractState.RECEIVED && !contract.isOnline && (
                <Button onClick={() => confirm(contract.id)} theme="success">
                  Утвердить
                </Button>
              )}

              {contract.state !== ContractState.CANCELLED &&
                (role === UserRole.ADMIN ||
                  (role === UserRole.MODERATOR &&
                    (contract.state === ContractState.APPROVED ||
                      contract.state === ContractState.RECEIVED ||
                      contract.state === ContractState.EDITED ||
                      contract.state === ContractState.AT_SIGNING_SSPVO))) && (
                  <Button
                    onClick={() =>
                      dispatch(
                        showModal({
                          name: 'CANCEL_CONTRACT',
                          data: { contractId: contract.id },
                        })
                      )
                    }
                  >
                    Аннулировать
                  </Button>
                )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ModeratorContractIdInfo;
